import { OrgPathCanvas } from '../components/OrgPathCanvas';
import { CustomPathCanvas } from '../components/CustomPathCanvas';
import { PathFlowsPanel } from '../components/PathFlowsPanel';
import { buildJsonPreview } from '../lib/buildJsonPreview';
import { pathModeOf } from '../lib/customPathModel';
import type { CycleDesignerFormState, OrgPathNode, PathMode } from '../types';
import type { CycleDesignerState } from '../hooks/useCycleDesignerState';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';

interface StepDesignPathsProps {
  form: CycleDesignerFormState;
  state: Pick<
    CycleDesignerState,
    | 'paths'
    | 'activePathId'
    | 'setActivePathId'
    | 'rightTab'
    | 'setRightTab'
    | 'toggleNode'
    | 'buildOrgById'
    | 'setNodeAssignment'
    | 'pendingFork'
    | 'confirmFork'
    | 'forkAsNewPath'
    | 'cancelFork'
    | 'setPathMode'
    | 'addCustomRung'
    | 'assignCustomRungRef'
    | 'removeCustomRungFromPath'
    | 'moveCustomRungOnPath'
  >;
  orgNodes: OrgPathNode[];
  anchor: OrgPathNode | null;
  maxRelativeLevel: number;
  anchorId: string;
  /** Level-0 label for custom mode — who holds the ball before the first toss. */
  customAnchorLabel: string;
  /** False when the workspace has no org chart to draw paths on. */
  orgChartAvailable: boolean;
  /** The cycle's owner: L0 names them and an ownerless unit falls back to them. */
  cycleOwner: CatchballParticipantRef | null;
}

export function StepDesignPaths({
  form,
  state,
  orgNodes,
  anchor,
  maxRelativeLevel,
  anchorId,
  customAnchorLabel,
  orgChartAvailable,
  cycleOwner,
}: StepDesignPathsProps) {
  const orgById = state.buildOrgById(orgNodes);
  const jsonPreview = buildJsonPreview(form, state.paths, orgNodes, anchor);
  const mode = pathModeOf(form);

  const changeMode = (next: PathMode) => {
    if (next === mode) return;
    // Stops are drawn against the current anchor; the other mode anchors
    // elsewhere, so switching cannot carry them over.
    const hasWork = state.paths.some((p) => p.nodes.length > 1);
    if (
      hasWork &&
      !window.confirm(
        'Switching how stops are named clears the stops you have added so far. Continue?',
      )
    ) {
      return;
    }
    state.setPathMode(next);
  };

  return (
    <div style={{ display: 'contents' }}>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderBottom: '1px solid var(--rule)',
            background: 'var(--bg-raised)',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: '0.65rem',
              letterSpacing: '0.14em',
              color: 'var(--text-dim)',
              textTransform: 'uppercase',
            }}
          >
            Stops come from
          </span>
          <div style={{ display: 'flex', border: '1px solid var(--rule-bright)' }}>
            <ModeButton
              active={mode === 'org_chart'}
              disabled={!orgChartAvailable}
              title={
                orgChartAvailable
                  ? 'Draw the ball path through the org chart under this cycle’s anchor'
                  : 'This workspace has no org chart yet'
              }
              onClick={() => changeMode('org_chart')}
            >
              Org chart
            </ModeButton>
            <ModeButton
              active={mode === 'custom'}
              title="Build your own order of people and groups for the ball to travel through"
              onClick={() => changeMode('custom')}
            >
              Custom
            </ModeButton>
          </div>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
            {mode === 'custom'
              ? 'Pick people or groups in the order the ball should reach them.'
              : 'Click org nodes to build the path down from the anchor.'}
          </span>
        </div>

        {mode === 'custom' ? (
          <CustomPathCanvas
            paths={state.paths}
            activePathId={state.activePathId}
            anchorLabel={customAnchorLabel}
            onAddRung={state.addCustomRung}
            onAssignRung={state.assignCustomRungRef}
            onRemoveRung={state.removeCustomRungFromPath}
            onMoveRung={state.moveCustomRungOnPath}
          />
        ) : (
          <OrgPathCanvas
            orgNodes={orgNodes}
            paths={state.paths}
            activePathId={state.activePathId}
            maxRelativeLevel={maxRelativeLevel}
            anchorId={anchorId}
            onToggleNode={(nodeId) => state.toggleNode(nodeId, orgById)}
            onAssignNode={state.setNodeAssignment}
            cycleOwner={cycleOwner}
            pendingFork={state.pendingFork}
            onConfirmFork={() => state.confirmFork(orgById)}
            onForkAsNewPath={() => state.forkAsNewPath(orgById)}
            onCancelFork={state.cancelFork}
          />
        )}
      </div>
      <PathFlowsPanel
        tab={state.rightTab}
        onTabChange={state.setRightTab}
        paths={state.paths}
        orgNodes={orgNodes}
        jsonPreview={jsonPreview}
        cycleOwner={cycleOwner}
        onRemoveStop={(pathId, nodeId) => {
          // toggleNode acts on the active path, so make the stop's own path
          // active before removing — otherwise the × on one path's list would
          // edit whichever path happened to be selected.
          if (pathId !== state.activePathId) state.setActivePathId(pathId);
          state.toggleNode(nodeId, orgById);
        }}
      />
    </div>
  );
}

function ModeButton({
  active,
  disabled,
  title,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: '5px 12px',
        background: active ? 'var(--mustard)' : 'transparent',
        border: 'none',
        color: active ? 'var(--bg)' : disabled ? 'var(--rule-bright)' : 'var(--text-ter)',
        fontFamily: 'var(--mono)',
        fontSize: '0.7rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}
